<!--
    @author bluefirex
-->
<template>
    <div class="acquisit-state-inspector-submission default-wrapper">
	    <StateInspectorHeader>
		    <template #default>
			    <h2>Submission Payload</h2>
			    <span class="flag size">{{ submissionPayloadHumanSize }}</span>
			    
			    <span class="flag pdf-size" v-if="pdfSizeCalculating">
					PDF: <acquisit-spinner size="tiny" color="white" />
				</span>
			    
			    <span :class="['flag', 'pdf-size', { exceeded: pdfSize + submissionPayloadSize > 1024 * 1024 }]" v-else-if="pdfSize !== undefined">
					PDF: {{ pdfHumanSize }}
				</span>
			    
			    <span class="flag pdf-size" v-else>
					PDF: ?
					<acquisit-button label="Calculate" type="text" color="white" @click="calculatePDFSize" />
				</span>
		    </template>
		    
		    <template #actions>
			    <acquisit-checkbox v-model="showValidatedPayloadOnly"
			                       label="Show validated only" />
			    
			    <acquisit-button label="Copy"
			                     @click="copySubmissionToClipboard" />
		    </template>
	    </StateInspectorHeader>
	    
	    <StateInspectorJsonView :json="submissionPayload" colored />
	    
	    <Toast ref="$clipboardToast" content="Copied to clipboard" type="info" />
    </div>
</template>

<script setup lang="ts">
	import StateInspectorJsonView from '@ui/dev/StateInspectorJsonView.vue'
	import StateInspectorHeader from '@ui/dev/StateInspectorHeader.vue'
	import { computed, onMounted, ref, watch } from 'vue'
	import { bytesToHuman, copyToClipboard } from '@Visma-Real-Estate-Solutions/acquisit-ui-vue/functions'
	import { useGenericStore } from '@/stores/generic.ts'
	import { useProtocolStore } from '@/stores/protocol.ts'
	import { useScreenshotsStore } from '@/stores/screenshots.ts'
	import Toast from '@ui/core/Toast.vue'
	
	const submissionPayload = ref<any>({})
	const protocolStore = useProtocolStore()
	const screenshotsStore = useScreenshotsStore()
	
	const $clipboardToast = ref<typeof Toast|null>()
	
	const createSubmissionPayload = async () => {
		try {
			submissionPayload.value = await protocolStore.createSubmissionPayload()
			submissionPayload.value.ui = '<omitted from preview>'
		} catch (e: any) {
			console.error('Error generating submission_payload', e)
			
			submissionPayload.value = {
				error: e.message,
				name: e.name,
				fileName: e.fileName,
				lineNumber: e.lineNumber,
				stack: e.stack
			}
		}
	}
	
	watch(submissionPayload, () => {
		pdfSize.value = undefined
	}, {
		deep: true
	})
	
	const submissionPayloadSize = computed(() => JSON.stringify(submissionPayload.value).length)
	const submissionPayloadHumanSize = computed(() => bytesToHuman(submissionPayloadSize.value))
	
	const pdfSize = ref<number|undefined>(undefined)
	const pdfSizeCalculating = ref(false)
	const pdfHumanSize = computed(() => pdfSize.value === undefined ? undefined : bytesToHuman(pdfSize.value))
	
	const calculatePDFSize = async () => {
		pdfSizeCalculating.value = true
		
		try {
			const pdf = await screenshotsStore.createPDF()
			pdfSize.value = String(pdf).length
		} finally {
			pdfSizeCalculating.value = false
		}
	}
	
	const showValidatedPayloadOnly = computed({
		get() {
			return useGenericStore().dev.submission_payload_validated_only
		},
		
		set(value) {
			useGenericStore().setDevSubmissionPayloadValidatedOnly(value)
			createSubmissionPayload()
		}
	})
	
	const copySubmissionToClipboard = async () => {
		try {
			const str = JSON.stringify(submissionPayload.value)
			await copyToClipboard(str)
			
			if ($clipboardToast.value) {
				$clipboardToast.value.open(3000)
			} else {
				alert('Copied to clipboard')
			}
		} catch (e) {
			console.error('Failed to create submission JSON', e)
			alert('Could not copy to clipboard, check console for more details')
		}
	}
	
	onMounted(() => {
		createSubmissionPayload()
	})
</script>

<style lang="scss">
    @use '@/assets/mixins.scss' as m;
    
    .acquisit-state-inspector-submission default-wrapper {
    
    }
</style>